async function http<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { ...((opts.headers as any) ?? {}) };
  if (opts.body != null && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const r = await fetch(path, {
    credentials: 'include',
    ...opts,
    headers
  });
  if (!r.ok) {
    let detail: any = null;
    try { detail = await r.json(); } catch {}
    throw new HttpError(r.status, detail?.message ?? detail?.error ?? r.statusText, detail);
  }
  return r.status === 204 ? (undefined as any) : r.json();
}

export class HttpError extends Error {
  constructor(public status: number, message: string, public detail?: any) { super(message); }
}

export const api = {
  get:  <T = any>(path: string) => http<T>(path),
  post: <T = any>(path: string, body?: unknown) => http<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  me: () => http<{ user: User; permissions?: string[] }>('/api/me'),
  // Com o código por e-mail ligado, a senha certa devolve `codigo` (sem sessão) e a sessão sai em loginCodigo.
  login: (email: string, password: string) => http<{ user?: User; codigo?: { desafio: string; email: string; validadeMin: number } }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  loginCodigo: (desafio: string, codigo: string) => http<{ user: User }>('/api/auth/login/codigo', { method: 'POST', body: JSON.stringify({ desafio, codigo }) }),
  loginGoogle: (credential: string) => http<{ user: User }>('/api/auth/google', { method: 'POST', body: JSON.stringify({ credential }) }),
  logout: () => http('/api/auth/logout', { method: 'POST' }),
  changeMyPassword: (currentPassword: string, newPassword: string) =>
    http<{ ok: true }>('/api/me/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  updateMyProfile: (data: { name?: string | null; phone?: string | null }) =>
    http<{ user: User }>('/api/me', { method: 'PATCH', body: JSON.stringify(data) }),

  // Vendors
  vendors: () => http<{ vendors: Vendor[] }>('/api/vendors'),
  vendorCreate: (data: Partial<Vendor>) => http<{ vendor: Vendor }>('/api/vendors', { method: 'POST', body: JSON.stringify(data) }),
  vendorUpdate: (id: number, data: Partial<Vendor>) => http<{ vendor: Vendor }>(`/api/vendors/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  vendorDelete: (id: number) => http(`/api/vendors/${id}`, { method: 'DELETE' }),

  // Models
  models: (vendor?: string) => http<{ models: Model[] }>(`/api/models${vendor ? `?vendor=${vendor}` : ''}`),
  modelCreate: (data: Partial<Model> & { vendorId: number; code: string; driverClass: string }) =>
    http<{ model: Model }>('/api/models', { method: 'POST', body: JSON.stringify(data) }),
  modelUpdate: (id: number, data: Partial<Model>) => http<{ model: Model }>(`/api/models/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  modelDelete: (id: number) => http(`/api/models/${id}`, { method: 'DELETE' }),

  // Devices
  devices: (params: Record<string, string | number | boolean> = {}) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== '' && v != null).map(([k, v]) => [k, String(v)])));
    return http<{ total: number; take: number; skip: number; devices: Device[] }>(`/api/devices?${q}`);
  },
  device: (id: number) => http<{ device: Device & { vendor: Vendor; model: Model | null; interfaces: any[]; accesses: any[] } }>(`/api/devices/${id}`),
  deviceCreate: (data: Partial<Device> & { vendorId: number; hostname: string; mgmtIp: string }) =>
    http<{ device: Device }>('/api/devices', { method: 'POST', body: JSON.stringify(data) }),
  deviceUpdate: (id: number, data: Partial<Device>) => http<{ device: Device }>(`/api/devices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deviceDelete: (id: number, confirm = false) => http(`/api/devices/${id}${confirm ? '?confirm=true' : ''}`, { method: 'DELETE' }),

  // DeviceAccess
  deviceAccessUpsert: (deviceId: number, body: any) =>
    http(`/api/devices/${deviceId}/accesses`, { method: 'POST', body: JSON.stringify(body) }),
  deviceAccessDelete: (deviceId: number, accessId: number) =>
    http(`/api/devices/${deviceId}/accesses/${accessId}`, { method: 'DELETE' }),
  deviceAccessTest: (deviceId: number, body: any) =>
    http<{ ok: boolean; latencyMs: number; info?: string; error?: string }>(`/api/devices/${deviceId}/accesses/test`, { method: 'POST', body: JSON.stringify(body) }),

  // Admin — workers
  adminWorkers: () => http<{ kind: string; total: number; rows: Array<{
    name: string; queueName: string; kind: 'cron' | 'tail' | 'on-demand';
    schedule: string; concurrency: number; trigger: string; source: string; description: string;
    workersAlive: number; paused: boolean;
    jobs: { waiting: number; active: number; completed: number; failed: number; delayed: number };
  }> }>('/api/admin/workers'),
  adminWorkerErrors: (params: { queue?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.queue && params.queue !== 'ALL') q.set('queue', params.queue);
    if (params.limit) q.set('limit', String(params.limit));
    return http<{ kind: string; queue: string; total: number; errors: Array<{
      queueName: string; workerName: string; jobId: string; jobName: string;
      failedReason: string | null; stacktrace: string | null; attemptsMade: number | null;
      timestamp: number | null; finishedOn: number | null; processedOn: number | null;
      data: any;
    }> }>(`/api/admin/workers/errors?${q}`);
  },
  adminWorkerErrorsClean: (queue: string) =>
    http<{ ok: boolean; queue: string; total: number; results: Array<{ queueName: string; removed: number }> }>(
      '/api/admin/workers/errors/clean', { method: 'POST', body: JSON.stringify({ queue }) }
    ),
  adminWorkerErrorsRetry: (queue: string, jobId?: string) =>
    http<{ ok: boolean; queue: string; jobId: string | null; total: number; results: Array<{ queueName: string; retried: number; error?: string }> }>(
      '/api/admin/workers/errors/retry', { method: 'POST', body: JSON.stringify({ queue, jobId: jobId ?? null }) }
    ),

  // Pops
  pops: (q?: string) => http<{ pops: Pop[] }>(`/api/pops${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  pop: (id: number) => http<{ pop: Pop & { devices: any[]; _count: { devices: number } } }>(`/api/pops/${id}`),
  popCreate: (data: Partial<Pop> & { code: string; name: string }) => http<{ pop: Pop }>('/api/pops', { method: 'POST', body: JSON.stringify(data) }),
  popUpdate: (id: number, data: Partial<Pop>) => http<{ pop: Pop }>(`/api/pops/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  popDelete: (id: number) => http(`/api/pops/${id}`, { method: 'DELETE' }),
  popGeocode: (address: string) => http<{ lat: number; lng: number; formattedAddress: string }>(`/api/pops/geocode`, { method: 'POST', body: JSON.stringify({ address }) }),

  // Interfaces
  ifaceCreate: (deviceId: number, name: string) =>
    http<{ iface: { id: number; name: string } }>(`/api/devices/${deviceId}/interfaces`, { method: 'POST', body: JSON.stringify({ name }) }),
  ifaceDiscover: (deviceId: number) =>
    http<{ kind: string; created: number; updated: number; total: number }>(`/api/devices/${deviceId}/interfaces/discover`, { method: 'POST' }),
  zbxInterfaces: (deviceId: number) =>
    http<{ total: number; interfaces: Array<{ name: string; description: string | null; ifaceId: number | null; source: 'local' | 'zabbix' | 'local+zabbix'; itemCount: number }> }>(`/api/devices/${deviceId}/zabbix-interfaces`),
  zbxItemsImport: (deviceId: number) =>
    http<{ kind: string; total: number; created: number; updated: number }>(`/api/devices/${deviceId}/zabbix-items/import`, { method: 'POST' }),
  zbxItemsList: (deviceId: number, params: { search?: string; onlyOid?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.onlyOid) q.set('onlyOid', 'true');
    return http<{ total: number; items: ZbxItemRow[] }>(`/api/devices/${deviceId}/zabbix-items?${q}`);
  },
  zbxSyncHostIds: (force = false) =>
    http<{ kind: string; zbxHosts: number; devices: number; matched: number; byName: number; byIp: number; conflicts: number; untouched: number }>(`/api/zabbix/sync-hostids${force ? '?force=true' : ''}`, { method: 'POST' }),
  zbxProbeHost: (hostid: number) => http<{ host: { hostid: string; host: string; name: string; status: string } }>(`/api/zabbix/host/${hostid}/probe`),

  // PopLinks
  popLinks: () => http<{ links: PopLink[] }>('/api/pop-links'),
  popLink: (id: number) => http<{ link: PopLink }>(`/api/pop-links/${id}`),
  popLinkUpdate: (id: number, body: { pathGeometry?: Array<{lat:number;lng:number}> | null; pathSource?: string | null; sourceDeviceId?: number; destDeviceId?: number; sourceIfaceId?: number | null; destIfaceId?: number | null; sourceIfaceName?: string | null; destIfaceName?: string | null }) =>
    http<{ link: PopLink }>(`/api/pop-links/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  popLinkCreate: (body: { sourcePopId: number; destPopId: number; type: 'IBGP'|'EBGP'|'LLDP'|'STATIC'; sourceDeviceId?: number; destDeviceId?: number; sourceIfaceId?: number; destIfaceId?: number; sourceIfaceName?: string; destIfaceName?: string }) =>
    http<{ link: PopLink }>('/api/pop-links', { method: 'POST', body: JSON.stringify(body) }),
  // DeviceLink (intra-POP)
  deviceLinks: (popId?: number) => http<{ total: number; links: DeviceLink[] }>(`/api/device-links${popId ? '?popId=' + popId : ''}`),
  deviceLinkCreate: (body: { deviceAId: number; deviceBId: number; ifaceAName?: string; ifaceBName?: string; type?: string; description?: string }) =>
    http<{ link: DeviceLink }>('/api/device-links', { method: 'POST', body: JSON.stringify(body) }),
  deviceLinkUpdate: (id: number, body: any) => http<{ link: DeviceLink }>(`/api/device-links/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deviceLinkDelete: (id: number) => http(`/api/device-links/${id}`, { method: 'DELETE' }),
  popLinkLive: (id: number) =>
    http<{ kind: string; source: LiveSnap; dest: LiveSnap }>(`/api/pop-links/${id}/live`),
  deviceLinkLive: (id: number) =>
    http<{ kind: string; source: LiveSnap; dest: LiveSnap }>(`/api/device-links/${id}/live`),
  popLinkDelete: (id: number) => http(`/api/pop-links/${id}`, { method: 'DELETE' }),
  popLinkApprove: (id: number) => http<{ link: PopLink }>(`/api/pop-links/${id}/approve`, { method: 'POST' }),
  deviceLinkApprove: (id: number) => http<{ link: DeviceLink }>(`/api/device-links/${id}/approve`, { method: 'POST' }),
  topologyDiscoverLldp: () => http<{ kind: string; devices: number; ok: number; noDriver: number; noSsh: number; failed: number; rawUpserts?: number; neighbors: number; lldpNames: number }>('/api/lldp/discover', { method: 'POST' }),

  // LLDP triagem manual
  lldpNeighbors: (params: { includeDismissed?: boolean; includeLinked?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.includeDismissed) q.set('includeDismissed', 'true');
    if (params.includeLinked) q.set('includeLinked', 'true');
    return http<{ total: number; neighbors: LldpRawRow[] }>(`/api/lldp/neighbors${q.toString() ? '?' + q : ''}`);
  },
  lldpDismiss: (id: number, dismissed = true) =>
    http<{ neighbor: LldpRawRow }>(`/api/lldp/neighbors/${id}/dismiss`, { method: 'POST', body: JSON.stringify({ dismissed }) }),
  lldpConvert: (id: number, targetDeviceId: number) =>
    http<{ kind: 'PopLink' | 'DeviceLink'; link: any }>(`/api/lldp/neighbors/${id}/convert`, { method: 'POST', body: JSON.stringify({ targetDeviceId }) }),
  topologyDiscover: () => http<{ kind: string; devices: number; created: number; updated: number }>('/api/topology/discover', { method: 'POST' }),

  // Optical
  opticalOverview: () => http<OpticalOverview>('/api/optical/overview'),
  deviceOpticalSnapshot: (deviceId: number) =>
    http<{
      cards: Array<{
        ifName: string; ifaceId: number | null; hasHistory: boolean;
        transceiverModel: string | null; transceiverVendor: string | null;
        partNumber: string | null; mediaType: string | null; wavelengthNm: string | null;
        rxDbm: number | null; txDbm: number | null;
        rxLowDbm: number | null; rxHighDbm: number | null;
        txLowDbm: number | null; txHighDbm: number | null;
        temperature: number | null; voltage: number | null; biasMa: number | null;
        status: 'ok' | 'warning' | 'critical' | 'no-data';
      }>;
      lastSync: string | null;
      zbxError: string | null;
      hasZabbix: boolean;
      hint: string | null;
    }>(`/api/devices/${deviceId}/optical-snapshot`),
  deviceInterfacesSnapshot: (deviceId: number) =>
    http<{
      interfaces: Array<{
        ifaceId: number | null; name: string; ifIndex: number | null; description: string | null;
        adminUp: boolean | null; operUp: boolean | null; operStatusRaw: number | null;
        speedMbps: number | null; speedBpsLive: number | null;
        hasOptic: boolean; transceiverModel: string | null; transceiverVendor: string | null; mediaType: string | null;
        inBps: number | null; outBps: number | null;
        inDiscards: number | null; outDiscards: number | null;
        inErrors: number | null; outErrors: number | null;
        lldpPeer: { hostname: string; remoteIface: string | null; state: string } | null;
      }>;
      lastSync: string | null;
      zbxError: string | null;
      hasZabbix: boolean;
    }>(`/api/devices/${deviceId}/interfaces/snapshot`),
  opticalHistory: (ifaceId: number, params: { from?: string; to?: string; bucket?: 'raw' | '5m' | '1h' } = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([_, v]) => v != null).map(([k, v]) => [k, String(v)]));
    return http<{ iface: any; series: any[] }>(`/api/interfaces/${ifaceId}/optical?${q}`);
  },

  // BGP
  bgpOverview: () => http<BgpOverview>('/api/bgp/overview'),
  bgpPeers: (deviceId: number) => http<{ total: number; removed?: number; peers: BgpPeer[] }>(`/api/devices/${deviceId}/bgp-peers`),
  bgpEvents: (limit = 100) => http<{ events: BgpEvent[] }>(`/api/bgp/events?limit=${limit}`),
  // Desativar/reativar peers BGP em massa (borda Huawei)
  bgpAdminPreview: (body: { cidrs?: string[]; transportId?: number; asns?: number[]; deviceIds?: number[] }) =>
    http<BgpAdminPreview>('/api/bgp/admin/preview', { method: 'POST', body: JSON.stringify(body) }),
  bgpAdminApply: (body: { enabled: boolean; reason: string; confirm: string; items: { deviceId: number; peerIp: string }[] }) =>
    http<BgpAdminApplyResult>('/api/bgp/admin/apply', { method: 'POST', body: JSON.stringify(body) }),
  bgpTransports: () => http<{ transports: BgpTransport[] }>('/api/bgp/transports'),
  bgpTransportSave: (body: { name: string; cidrs: string[] }) =>
    http<{ transport: BgpTransport }>('/api/bgp/transports', { method: 'POST', body: JSON.stringify(body) }),
  bgpTransportDelete: (id: number) => http<{ ok: true }>(`/api/bgp/transports/${id}`, { method: 'DELETE' }),

  summary: () => http<Summary>('/api/devices/stats/summary'),

  // F10 — Firmware
  firmwares: (params: { modelId?: number; vendorId?: number; kind?: 'SYSTEM' | 'PATCH' | 'BUNDLE'; q?: string; take?: number; skip?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.modelId)  sp.set('modelId',  String(params.modelId));
    if (params.vendorId) sp.set('vendorId', String(params.vendorId));
    if (params.kind)     sp.set('kind', params.kind);
    if (params.q)        sp.set('q', params.q);
    if (params.take)     sp.set('take', String(params.take));
    if (params.skip)     sp.set('skip', String(params.skip));
    return http<{ total: number; take: number; skip: number; items: FirmwareRow[] }>(`/api/firmwares?${sp}`);
  },
  firmware: (id: number) => http<FirmwareDetail>(`/api/firmwares/${id}`),
  firmwareDelete: (id: number, confirm = false) =>
    http(`/api/firmwares/${id}${confirm ? '?confirm=true' : ''}`, { method: 'DELETE' }),
  firmwareDownloadUrl: (id: number) => `/api/firmwares/${id}/download`,
  firmwareFreeSpace: () => http<{ freeBytes: number; totalBytes: number; usedBytes: number; maxUploadBytes: number; safetyBufferBytes: number }>(`/api/firmwares/free-space`),
  firmwareCapabilities: () => http<{ drivers: Record<string, { upload: boolean; applySystem: boolean; applySystemReboots: boolean; applyPatch: boolean; reboot: boolean; displayStartup: boolean; verify: boolean; listFlash: boolean; deleteFlash: boolean; ifaceAdmin: boolean; ifaceAdminPersistent: boolean }> }>(`/api/firmwares/driver-capabilities`),
  /** Upload via XHR (não fetch) pra suportar progress event. */
  firmwareUpload: (
    body: { modelId: number; version: string; kind?: FirmwareKind; notes?: string; file: File },
    onProgress?: (loaded: number, total: number) => void
  ) => new Promise<{ id: number; modelId: number; kind: FirmwareKind; version: string; sha256: string; sizeBytes: number; reused: boolean }>((resolve, reject) => {
    const fd = new FormData();
    fd.append('modelId', String(body.modelId));
    fd.append('version', body.version);
    if (body.kind) fd.append('kind', body.kind);
    if (body.notes) fd.append('notes', body.notes);
    fd.append('file', body.file, body.file.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/firmwares');
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    }
    xhr.onload = () => {
      let detail: any = null;
      try { detail = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(detail);
      else reject(new HttpError(xhr.status, detail?.error ?? xhr.statusText, detail));
    };
    xhr.onerror = () => reject(new HttpError(0, 'network_error'));
    xhr.send(fd);
  }),
  // ---- Manuais (Onda 2 do catálogo) ----
  manualsList: (modelId: number) =>
    http<{ manuals: Array<{
      id: number; title: string; originalFilename: string; mimeType: string;
      sha256: string; sizeBytes: number; notes: string | null;
      uploadedAt: string;
      uploadedBy: { id: number; email: string; name: string | null } | null;
    }> }>(`/api/models/${modelId}/manuals`),
  manualUpload: (
    body: { modelId: number; title?: string; notes?: string; file: File },
    onProgress?: (loaded: number, total: number) => void
  ) => new Promise<{ id: number; modelId: number; title: string; sha256: string; sizeBytes: number; reused: boolean }>((resolve, reject) => {
    const fd = new FormData();
    if (body.title) fd.append('title', body.title);
    if (body.notes) fd.append('notes', body.notes);
    fd.append('file', body.file, body.file.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/models/${body.modelId}/manuals`);
    xhr.withCredentials = true;
    if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    xhr.onload = () => {
      let detail: any = null;
      try { detail = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(detail);
      else reject(new HttpError(xhr.status, detail?.error ?? xhr.statusText, detail));
    };
    xhr.onerror = () => reject(new HttpError(0, 'network_error'));
    xhr.send(fd);
  }),
  manualUpdate: (id: number, body: { title?: string; notes?: string | null }) =>
    http<{ id: number; title: string; notes: string | null }>(`/api/manuals/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  manualDelete: (id: number) =>
    http(`/api/manuals/${id}?confirm=true`, { method: 'DELETE' }),
  manualDownloadUrl: (id: number) => `/api/manuals/${id}/download`,
  manualPreviewUrl: (id: number) => `/api/manuals/${id}/preview`,

  // ---- Comandos por modelo (Onda 3 do catálogo) ----
  commandsList: (modelId: number) =>
    http<{ commands: Array<{
      id: number; modelId: number; name: string; category: string | null;
      template: string; description: string | null; destructive: boolean;
      rolesAllowed: Array<'VIEWER' | 'OPERATOR' | 'ADMIN'>;
    }> }>(`/api/models/${modelId}/commands`),
  commandCreate: (modelId: number, body: {
    name: string; category?: string | null; template: string;
    description?: string | null; destructive?: boolean;
    rolesAllowed?: Array<'VIEWER' | 'OPERATOR' | 'ADMIN'>;
  }) => http<{ command: any }>(`/api/models/${modelId}/commands`, { method: 'POST', body: JSON.stringify(body) }),
  commandUpdate: (id: number, body: Partial<{
    name: string; category: string | null; template: string;
    description: string | null; destructive: boolean;
    rolesAllowed: Array<'VIEWER' | 'OPERATOR' | 'ADMIN'>;
  }>) => http<{ command: any }>(`/api/commands/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  commandDelete: (id: number) => http(`/api/commands/${id}`, { method: 'DELETE' }),

  // ---- ONUs (F9.1) ----
  oltOnus: (deviceId: number) =>
    http<{
      olt: { id: number; hostname: string };
      onus: Array<{
        id: number; serialNumber: string; gponPort: number; onuId: number;
        vendorId: string | null; model: string | null; softwareVersion: string | null;
        description: string | null; status: string;
        firstSeenAt: string; lastSeenAt: string | null;
        oltRxDbm: number | null; onuRxDbm: number | null; distanceKm: number | null;
        uptimeSec: number | null; operStatus: string | null; configStatus: string | null;
        lastReadingTs: string | null;
      }>;
      summary: {
        total: number; online: number; offline: number;
        byGpon: Record<string, number>;
        byVendor: Record<string, number>;
        byModel: Record<string, number>;
      };
    }>(`/api/olts/${deviceId}/onus`),
  onuHistory: (sn: string, range: '1h' | '6h' | '24h' | '7d' | '30d' = '24h') =>
    http<{
      onu: any;
      range: string;
      series: Array<{ ts: string; oltRxDbm: number | null; onuRxDbm: number | null; distanceKm: number | null; uptimeSec: number | null; operStatus: string | null }>;
    }>(`/api/onus/${encodeURIComponent(sn)}/history?range=${range}`),
  onuUpdate: (sn: string, body: { description?: string | null }) =>
    http<{ description: string | null }>(`/api/onus/${encodeURIComponent(sn)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  onusOverview: () => http<{
    byStatus: Array<{ status: string; total: number }>;
    worstOlts: Array<{ oltDeviceId: number; hostname: string; mgmtIp: string; total: number; offline: number; avgRx: number | null; worstRx: number | null }>;
    worstOnus: Array<{ serialNumber: string; vendorId: string | null; model: string | null; oltDeviceId: number; hostname: string; mgmtIp: string; gponPort: number; onuId: number; status: string; lastRx: number | null; lastTs: string | null; description: string | null }>;
    byUf: Array<{ uf: string | null; total: number; offline: number }>;
    byVendor: Array<{ vendorId: string | null; total: number }>;
  }>(`/api/onus/overview`),
  onusSearch: (q: string) => http<{
    hits: Array<{ serialNumber: string; vendorId: string | null; model: string | null; description: string | null; status: string; oltDeviceId: number; hostname: string; gponPort: number; onuId: number }>;
  }>(`/api/onus/search?q=${encodeURIComponent(q)}`),
  healthOverview: () => http<HealthOverview>('/api/health/overview'),
  oltGponSummary: (id: number) => http<{
    olt: { id: number; hostname: string };
    thresholds: { warningDbm: number; criticalDbm: number };
    summary: GponAgg;
    ports: Array<GponAgg & { gponPort: number }>;
  }>(`/api/olts/${id}/gpon-summary`),
  onusSignal: () => http<{
    span: { since: string | null; until: string | null; hours: number | null };
    thresholds: { warningDbm: number; criticalDbm: number; minDropDb: number };
    summary: { withReading: number; warning: number; critical: number };
    worst: Array<{ serialNumber: string; vendorId: string | null; model: string | null; status: string; oltDeviceId: number; hostname: string; uf: string | null; gponPort: number; onuId: number; lastRx: number | null; lastTs: string | null }>;
    degrading: Array<{ serialNumber: string; vendorId: string | null; model: string | null; status: string; oltDeviceId: number; hostname: string; uf: string | null; gponPort: number; onuId: number; rxEarly: number; rxRecent: number; deltaDb: number; samplesEarly: number; samplesRecent: number }>;
  }>(`/api/onus/signal`),
  onusFlapping: (hours = 24) => http<{
    windowHours: number;
    summary: { flappers: number; unstablePons: number };
    flappers: Array<{ serialNumber: string; downs: number; ups: number; transitions: number; vendorId: string | null; model: string | null; status: string; gponPort: number; onuId: number; oltDeviceId: number; hostname: string; uf: string | null }>;
    unstablePons: Array<{ oltDeviceId: number; hostname: string; uf: string | null; gponPort: number; onusDistinct: number; totalDowns: number; onusOnPon: number; kind: 'flapping' | 'outage' }>;
  }>(`/api/onus/flapping?hours=${hours}`),
  onusSanitation: (days = 3) => http<{
    params: { days: number; limit: number };
    span: { since: string | null; spandays: number | null };
    summary: { candidates: number; neverActive: number; truncated: boolean };
    byOlt: Array<{ oltDeviceId: number; hostname: string; uf: string | null; candidates: number }>;
    onus: Array<{ serialNumber: string; oltDeviceId: number; hostname: string; uf: string | null; gponPort: number; onuId: number; vendorId: string | null; model: string | null; description: string | null; status: string; firstSeenAt: string; lastActiveAt: string | null; neverActive: boolean; darkDays: number; lastRx: number | null }>;
  }>(`/api/onus/sanitation?days=${days}`),
  // F9.1.n — modo limpeza de CTO
  ctoWatch: (id: number, gpon: number) => http<CtoWatch>(`/api/olts/${id}/cto-watch?gpon=${gpon}`),
  ctoWatchReset: (id: number, gpon: number) => http<{ ok: true }>(`/api/olts/${id}/cto-watch/reset?gpon=${gpon}`, { method: 'POST' }),
  ctoWatchCheck: (id: number, gpon: number) =>
    http<{ ok: true; checkedAt: string; stillDown: number[]; returned: Array<{ onuId: number; serialNumber: string }> }>(
      `/api/olts/${id}/cto-watch/check?gpon=${gpon}`, { method: 'POST' }),
  // IXC (ERP) — cliente a partir do MAC/serial da ONU (botão 👤 nas telas de sinal)
  ixcOnuLookup: (sn: string) => http<IxcOnuLookup>(`/api/ixc/onu/${encodeURIComponent(sn)}`),
  ixcSearch: (type: 'login' | 'name' | 'document', q: string) =>
    http<{ configured: boolean; results: Array<{ login: IxcLogin | null; cliente: IxcCliente | null }> }>(
      `/api/ixc/search?type=${type}&q=${encodeURIComponent(q)}`),
  commandExecAudit: (id: number, deviceId: number) =>
    http<{ ok: true }>(`/api/commands/${id}/exec-audit`, { method: 'POST', body: JSON.stringify({ deviceId }) }),
  command: (id: number) => http<{ command: any }>(`/api/commands/${id}`),

  // F10.14 — ações granulares
  firmwareTransfer: (deviceId: number, firmwareId: number) =>
    http<{ deploymentId: number; jobId: string }>(`/api/devices/${deviceId}/firmware/transfer`, { method: 'POST', body: JSON.stringify({ firmwareId }) }),
  firmwareApplySystem: (deviceId: number, firmwareId: number, confirmHostname?: string) =>
    http<{ deploymentId: number; jobId: string }>(`/api/devices/${deviceId}/firmware/apply-system`, {
      method: 'POST',
      body: JSON.stringify(confirmHostname ? { firmwareId, confirmHostname } : { firmwareId })
    }),
  firmwareApplyPatch: (deviceId: number, firmwareId: number) =>
    http<{ deploymentId: number; jobId: string }>(`/api/devices/${deviceId}/firmware/apply-patch`, { method: 'POST', body: JSON.stringify({ firmwareId }) }),
  firmwareReboot: (deviceId: number, confirmHostname: string) =>
    http<{ deploymentId: number; jobId: string }>(`/api/devices/${deviceId}/firmware/reboot`, { method: 'POST', body: JSON.stringify({ confirmHostname }) }),
  firmwareCheckStartup: (deviceId: number) =>
    http<{ deploymentId: number; jobId: string }>(`/api/devices/${deviceId}/firmware/check-startup`, { method: 'POST', body: JSON.stringify({}) }),
  deviceInterfaceAdminState: (deviceId: number, body: { ifName: string; enabled: boolean; reason: string; confirmHostname: string }) =>
    http<IfaceAdminResult>(`/api/devices/${deviceId}/interfaces/admin-state`, { method: 'POST', body: JSON.stringify(body) }),
  // Aba 🖼 Painel — lido do equipamento por SSH (cache 15 s / 4 s no backend)
  deviceFaceplate: (deviceId: number, withChassis = false) =>
    http<{ at: string; cached: boolean; ports: PortStatus[]; chassis?: ChassisSlot[]; chassisAt?: string; chassisError?: string }>(
      `/api/devices/${deviceId}/faceplate${withChassis ? '?chassis=1' : ''}`),
  deviceInterfaceLive: (deviceId: number, ifName: string) =>
    http<InterfaceLive>(`/api/devices/${deviceId}/interfaces/live?ifName=${encodeURIComponent(ifName)}`),
  // F10.16 — Storage management
  // F11 — Dashboard summary consolidado
  deviceDashboardSummary: (deviceId: number) =>
    http<{
      bgp: { total: number; byState: Record<string, number> };
      interfaces: { total: number };
      optical: { ports: Array<{ ifaceId: number; name: string; ts: string; rxDbm: number | null; txDbm: number | null }> };
      lastBackup: { id: number; capturedAt: string; sha8: string; format: string } | null;
      events24h: Array<{ hour: string; total: number; errors: number }>;
      recentEvents: Array<{ id: number; ts: string; severity: string; category: string | null; message: string }>;
      recentDeployments: Array<{ id: number; operation: string; status: string; startedAt: string; firmware: { id: number; version: string } | null }>;
      currentFirmwareVersion: string | null;
      recommendedFirmware: { id: number; version: string; filename: string; createdAt: string } | null;
      firmwareUpToDate: boolean;
    }>(`/api/devices/${deviceId}/dashboard-summary`),
  // F7 — eventos por device
  deviceEvents: (deviceId: number, params: { severity?: string; category?: string; search?: string; hours?: number; take?: number; skip?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.severity) sp.set('severity', params.severity);
    if (params.category) sp.set('category', params.category);
    if (params.search)   sp.set('search', params.search);
    if (params.hours)    sp.set('hours', String(params.hours));
    if (params.take != null) sp.set('take', String(params.take));
    if (params.skip != null) sp.set('skip', String(params.skip));
    return http<{
      deviceId: number;
      total: number;
      take: number;
      skip: number;
      events: Array<{ id: number; ts: string; severity: string; category: string | null; source: string; message: string; raw?: string | null }>;
    }>(`/api/devices/${deviceId}/events${sp.size ? `?${sp}` : ''}`);
  },
  deviceStorage: (deviceId: number) =>
    http<{
      hostname: string;
      totalKb: number;
      freeKb: number;
      usedKb: number;
      files: Array<{ name: string; size: number; modifiedAt: string; isDir: boolean }>;
    }>(`/api/devices/${deviceId}/storage`),
  deviceStorageDelete: (deviceId: number, body: { filenames: string[]; confirmHostname: string }) =>
    http<{
      deleted: number;
      total: number;
      results: Array<{ filename: string; ok: boolean; error?: string }>;
    }>(`/api/devices/${deviceId}/storage/delete`, { method: 'POST', body: JSON.stringify(body) }),
  firmwareBulkTransfer: (firmwareId: number, body: { deviceIds: number[]; confirmCount: number }) =>
    http<{
      enqueued: number;
      total: number;
      results: Array<{ deviceId: number; hostname?: string; ok: boolean; deploymentId?: number; jobId?: string; error?: string }>;
    }>(`/api/firmwares/${firmwareId}/bulk-transfer`, { method: 'POST', body: JSON.stringify(body) }),
  firmwareDeployment: (id: number) => http<FirmwareDeploymentDetail>(`/api/firmware-deployments/${id}`),
  deviceFirmwareDeployments: (deviceId: number) =>
    http<{ deployments: FirmwareDeploymentDetail[] }>(`/api/devices/${deviceId}/firmware-deployments`),
  firmwareDeploymentCancel: (id: number) =>
    http(`/api/firmware-deployments/${id}/cancel`, { method: 'POST' }),
  firmwareDeploymentsActive: () =>
    http<{ items: Array<{
      id: number; status: FirmwareDeploymentStatus; operation: FirmwareOperation; startedAt: string;
      progressFile: 'system'|'patch'|null; progressBytes: string|null; progressTotal: string|null; progressPct: number|null;
      firmware: { id: number; kind: FirmwareKind; version: string } | null;
      device: { id: number; hostname: string };
      startedBy: { id: number; email: string } | null;
    }> }>(`/api/firmware-deployments/active`),

  // F12 — Providers
  providers: (params: { kind?: ProviderKind; q?: string; enabled?: boolean } = {}) => {
    const sp = new URLSearchParams();
    if (params.kind) sp.set('kind', params.kind);
    if (params.q) sp.set('q', params.q);
    if (params.enabled !== undefined) sp.set('enabled', params.enabled ? 'true' : 'false');
    return http<{ providers: ProviderRow[] }>(`/api/providers?${sp}`);
  },
  provider: (id: number) =>
    http<{ provider: ProviderDetail; vinculacoes: ProviderVinculacoes }>(`/api/providers/${id}`),
  providerCreate: (data: Partial<Provider> & { code: string; name: string; kind: ProviderKind }) =>
    http<{ provider: Provider }>('/api/providers', { method: 'POST', body: JSON.stringify(data) }),
  providerUpdate: (id: number, data: Partial<Provider>) =>
    http<{ provider: Provider }>(`/api/providers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  providerDelete: (id: number, confirm = false) =>
    http(`/api/providers/${id}${confirm ? '?confirm=true' : ''}`, { method: 'DELETE' }),
  // Contatos
  providerContactCreate: (providerId: number, body: Partial<ProviderContact> & { name: string }) =>
    http<{ contact: ProviderContact }>(`/api/providers/${providerId}/contacts`, { method: 'POST', body: JSON.stringify(body) }),
  providerContactUpdate: (providerId: number, contactId: number, body: Partial<ProviderContact>) =>
    http<{ contact: ProviderContact }>(`/api/providers/${providerId}/contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  providerContactDelete: (providerId: number, contactId: number) =>
    http(`/api/providers/${providerId}/contacts/${contactId}`, { method: 'DELETE' }),
  // Procedimentos
  providerProcedureCreate: (providerId: number, body: Partial<ProviderProcedure> & { triggerType: string; title: string; description: string }) =>
    http<{ procedure: ProviderProcedure }>(`/api/providers/${providerId}/procedures`, { method: 'POST', body: JSON.stringify(body) }),
  providerProcedureUpdate: (providerId: number, procedureId: number, body: Partial<ProviderProcedure>) =>
    http<{ procedure: ProviderProcedure }>(`/api/providers/${providerId}/procedures/${procedureId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  providerProcedureDelete: (providerId: number, procedureId: number) =>
    http(`/api/providers/${providerId}/procedures/${procedureId}`, { method: 'DELETE' }),
  // Anexos
  providerAttachments: (providerId: number) =>
    http<{ attachments: ProviderAttachment[] }>(`/api/providers/${providerId}/attachments`),
  providerAttachmentDelete: (providerId: number, attachmentId: number) =>
    http(`/api/providers/${providerId}/attachments/${attachmentId}`, { method: 'DELETE' }),
  providerAttachmentDownloadUrl: (providerId: number, attachmentId: number) =>
    `/api/providers/${providerId}/attachments/${attachmentId}/download`,
  providerAttachmentUpload: (
    providerId: number,
    body: { file: File; notes?: string },
    onProgress?: (loaded: number, total: number) => void
  ) => new Promise<{ attachment: ProviderAttachment; reused: boolean }>((resolve, reject) => {
    const fd = new FormData();
    if (body.notes) fd.append('notes', body.notes);
    fd.append('file', body.file, body.file.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/providers/${providerId}/attachments`);
    xhr.withCredentials = true;
    if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    xhr.onload = () => {
      let detail: any = null;
      try { detail = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(detail);
      else reject(new HttpError(xhr.status, detail?.error ?? xhr.statusText, detail));
    };
    xhr.onerror = () => reject(new HttpError(0, 'network_error'));
    xhr.send(fd);
  }),

  // Admin — users
  users: () => http<{ users: UserAdminRow[] }>('/api/users'),
  userCreate: (data: { email: string; name?: string | null; phone?: string | null; password: string; role: 'VIEWER'|'OPERATOR'|'ADMIN'; groupIds: number[] }) =>
    http<{ user: UserAdminRow }>('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  userUpdate: (id: number, data: { name?: string | null; phone?: string | null; role?: 'VIEWER'|'OPERATOR'|'ADMIN'; active?: boolean; groupIds?: number[] }) =>
    http<{ user: UserAdminRow }>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  userResetPassword: (id: number) =>
    http<{ tempPassword: string }>(`/api/users/${id}/reset-password`, { method: 'POST' }),
  userDelete: (id: number) =>
    http(`/api/users/${id}?confirm=true`, { method: 'DELETE' }),

  // Admin — groups + permissions
  groups: () => http<{ groups: GroupRow[] }>('/api/groups'),
  groupCreate: (data: { name: string; description?: string | null; permissionCodes: string[] }) =>
    http<{ group: GroupRow }>('/api/groups', { method: 'POST', body: JSON.stringify(data) }),
  groupUpdate: (id: number, data: { name?: string; description?: string | null; permissionCodes?: string[] }) =>
    http<{ group: GroupRow }>(`/api/groups/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  groupDelete: (id: number) =>
    http(`/api/groups/${id}?confirm=true`, { method: 'DELETE' }),
  permissions: () => http<{ permissions: PermissionRow[] }>('/api/permissions')
};

export type UserAdminRow = {
  id: number;
  email: string;
  name: string | null;
  phone: string | null;
  role: 'VIEWER' | 'OPERATOR' | 'ADMIN';
  active: boolean;
  mustChangePw: boolean;
  createdAt: string;
  lastLogin: string | null;
  groups: { id: number; name: string }[];
};

export type GroupRow = {
  id: number;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  permissions: { code: string; description: string; category: string }[];
  userCount: number;
};

export type PermissionRow = {
  id: number;
  code: string;
  description: string;
  category: string;
  createdAt: string;
};

export type BgpPeer = {
  id: number; deviceId: number; peerIp: string; peerAsn: number | null;
  family: string; state: string;
  acceptedPfx: number | null; receivedPfx: number | null; sentPfx: number | null;
  uptimeSec: number | null; lastChange: string | null; description: string | null;
  updatedAt?: string; removed?: boolean;   // removed = sumiu do equipamento (linha parada no banco)
};
export type BgpTransport = { id: number; name: string; cidrs: string[]; createdAt: string; updatedAt: string };
export type BgpAdminPeer = {
  peerIp: string; peerAsn: number | null; adminDown: boolean; description: string | null; group: string | null;
  families: { family: string; state: string; receivedPfx: number | null; uptimeSec: number | null }[];
};
export type BgpAdminPreview = {
  cidrs: string[]; total: number;
  devices: { id: number; hostname: string; mgmtIp: string; source: 'live' | 'db'; checkedAt: string | null; error?: string; transit: string[]; peers: BgpAdminPeer[] }[];
};
export type BgpAdminApplyResult = {
  enabled: boolean;
  results: {
    deviceId: number; hostname: string; ok: boolean; error?: string; asn?: number; persistent?: boolean; transcript?: string;
    peers: { peerIp: string; before: string | null; after: string | null; changed: boolean; skipped: boolean }[];
  }[];
};
export type BgpOverview = {
  total: number;
  removed?: number;
  byState: Record<string, number>;
  devices: Array<{ id: number; hostname?: string; mgmtIp?: string; location?: string | null; asn?: number | null; vendor?: { code: string }; total: number; established: number; lastChange: string | null }>;
};
export type BgpEvent = {
  id: number; ts: string; severity: string; message: string; parsed: any;
  device: { id: number; hostname: string };
};

export type OpticalRow = {
  ifaceId: number; ifaceName: string; deviceId: number; hostname: string; mgmtIp: string;
  pop?: { code: string; uf: string | null; city: string | null } | null;
  transceiverModel: string | null; transceiverVendor: string | null;
  mediaType: string | null; wavelengthNm: string | null;
  rxLowDbm: number | null; rxHighDbm: number | null; txLowDbm: number | null; txHighDbm: number | null;
  lastTs: string | null; rxDbm: number | null; txDbm: number | null;
  lanes: Array<{ lane: number; rx: number | null; tx: number | null }> | null;
  status: 'ok' | 'warning' | 'critical' | 'no-data';
};
export type OpticalOverview = {
  summary: { total: number; ok: number; warning: number; critical: number; noData: number };
  rows: OpticalRow[];
};

export type PopLink = {
  id: number;
  sourcePopId: number; destPopId: number;
  sourceDeviceId: number; destDeviceId: number;
  sourceIfaceId: number | null; destIfaceId: number | null;
  type: 'IBGP' | 'EBGP' | 'LLDP' | 'STATIC';
  state: 'UP' | 'DOWN' | 'DEGRADED' | 'UNKNOWN';
  acceptedPfx: number | null;
  peerIp: string | null;
  lastSeen: string | null;
  lastChange: string | null;
  pathGeometry: Array<{lat:number;lng:number}> | null;
  pathSource: string | null;
  source?: string; approved?: boolean;
  sourcePop: Pop;
  destPop: Pop;
  sourceDevice: { id: number; hostname: string };
  destDevice: { id: number; hostname: string };
  sourceIface?: { id: number; name: string } | null;
  destIface?: { id: number; name: string } | null;
};

export type User = { id: number; email: string; name: string | null; phone: string | null; role: 'VIEWER'|'OPERATOR'|'ADMIN'; mustChangePw: boolean };
// GPON summary (tab 🔌 GPON do DevicePage) — espelha /api/olts/:id/gpon-summary
export type GponExtreme = { serialNumber: string; gponPort: number; onuId: number; description: string | null; rx: number } | null;
export type GponAgg = {
  total: number; online: number; withReading: number; warning: number; critical: number;
  avgRx: number | null; best: GponExtreme; worst: GponExtreme;
};
// F9.1.n — modo limpeza de CTO (espelha routes/cto-watch.ts)
export type CtoTransition = { at: string; onuId: number; serialNumber: string; kind: 'fell' | 'returned'; from: string; to: string };
export type CtoOnu = {
  onuId: number; serialNumber: string; vendorId: string | null; model: string | null;
  status: string; online: boolean; oltRxDbm: number | null;
  description: string | null; lastActiveAt: string | null; offlineDays: number | null;
  transition: CtoTransition | null;
};
export type CtoWatch = {
  mode: 'poll' | 'syslog' | 'unsupported';
  deviceId: number; hostname: string; gponPort: number; gponLabel: string;
  sessionStartedAt: string; lastPollAt: string | null; cached: boolean;
  onus: CtoOnu[]; transitions: CtoTransition[]; notes: string[];
};
// IXC (ERP) — espelha apps/api/src/lib/ixc.ts
export type IxcLogin = {
  id: string; login: string; onu_mac: string | null; mac: string | null; ip: string | null;
  ativo: string | null; online: string | null; id_cliente: string | null; id_contrato: string | null;
};
export type IxcCliente = {
  id: string; razao: string | null; fantasia: string | null; cnpj_cpf: string | null;
  telefone_celular: string | null; fone: string | null; whatsapp: string | null; email: string | null;
  endereco: string | null; numero: string | null; bairro: string | null; complemento: string | null; cep: string | null;
};
export type IxcOnuLookup = {
  configured: boolean; serialNumber: string; matchedBy: string | null; tried: string[];
  matches: Array<{ login: IxcLogin; cliente: IxcCliente | null }>;
};
export type Vendor = {
  id: number; code: string; name: string; description?: string | null; enabled?: boolean;
  _count?: { devices: number; models: number };
};
export type Model = {
  id: number; vendorId?: number; code: string; family: string | null; driverClass: string;
  description?: string | null; enabled?: boolean; defaults?: Record<string, unknown>;
  vendor?: { id: number; code: string; name: string };
  _count?: { devices: number };
};
export type Pop = {
  id: number; code: string; name: string;
  uf?: string | null; city?: string | null;
  street?: string | null; streetNumber?: string | null; neighborhood?: string | null;
  zipCode?: string | null; country?: string;
  latitude?: number | null; longitude?: number | null;
  notes?: string | null; enabled?: boolean;
  // F12 — Energia
  powerProviderId?: number | null;
  powerAccountNumber?: string | null;
  hasGenerator?: boolean;
  hasUps?: boolean;
  powerNotes?: string | null;
  powerProvider?: { id: number; code: string; name: string; kind: string } | null;
  _count?: { devices: number };
};
export type Device = {
  id: number; role: string; hostname: string; mgmtIp: string; port: number;
  popId: number | null; asn: number | null; description: string | null;
  monitored: boolean; reachable: boolean; lastSeen: string | null;
  bgpEnabled: boolean;
  opticalEnabled?: boolean;
  zabbixHostId?: number | null;
  vendorId?: number; modelId?: number | null;
  vendor: Vendor; model: Model | null;
  pop?: Pop | null;
  accesses?: Array<{ id: number; kind: string; port: number; username: string | null; preferred: boolean; baseUrl: string | null }>;
  currentFirmwareVersion?: string | null;
};
export type IfaceAdminState = 'UP' | 'DOWN' | 'ADMIN_DOWN' | 'UNKNOWN';
export type IfaceAdminResult = {
  ok: true; ifName: string; enabled: boolean; before: IfaceAdminState; after: IfaceAdminState;
  changed: boolean; persistent: boolean; transcript: string;
};
export type PortStatus = {
  name: string; state: IfaceAdminState; phy: string; protocol: string | null; mode?: string | null;
  inUtilPct: number | null; outUtilPct: number | null; inErrors: number | null; outErrors: number | null;
  description: string | null;
};
export type ChassisSlot = {
  slot: number; kind: string; online: string | null; register: string | null; status: string | null; role: string | null;
  boardType: string | null; description: string | null; picType: string | null; portCount: number | null;
  name?: string | null;
};
export type InterfaceLive = {
  ifName: string; at: string; cached?: boolean; state: IfaceAdminState; description: string | null;
  speedMbps: number | null; inRateBps: number | null; outRateBps: number | null; rateIsLive?: boolean;
  inBytes: number | null; outBytes: number | null; inUtilPct: number | null; outUtilPct: number | null;
  crc: number | null; inErrors: number | null; lastUp: string | null; lastDown: string | null;
  transceiver: null | {
    present: boolean; type?: string | null; vendor?: string | null; partNumber?: string | null;
    wavelengthNm?: string | null; distance?: string | null; temperatureC?: number | null;
    rxDbm?: number[]; txDbm?: number[];
    rxLowAlarm?: number | null; rxLowWarn?: number | null; rxHighWarn?: number | null; rxHighAlarm?: number | null;
    txLowAlarm?: number | null; txLowWarn?: number | null; txHighWarn?: number | null; txHighAlarm?: number | null;
  };
};
export type Summary = {
  total: number; reachable: number;
  byVendor: Record<string, number>;
  byRole: Record<string, number>;
  byModel: Record<string, number>;
};

export type HealthSeverity = 'critical' | 'warning' | 'ok';
export type HealthCategory = { key: string; label: string; severity: HealthSeverity; count: number; detail?: string; route: string };
export type HealthOverview = { categories: HealthCategory[]; rollup: { critical: number; warning: number; total: number }; generatedAt: string };

export type ZbxItemRow = {
  id: number; zabbixItemId: string;
  name: string; key_: string;
  valueType: number | null; delay: string | null; status: number | null;
  units: string | null; description: string | null;
  lastImportedAt: string;
};

export type LiveSnap = {
  ifaceName: string | null;
  trafficInBps: number | null;
  trafficOutBps: number | null;
  rxDbm: number | null;
  txDbm: number | null;
  operStatus: string | null;
  lastClock: number | null;
};

export type LldpRawRow = {
  id: number;
  deviceId: number;
  localIfaceName: string;
  remoteSysName: string | null;
  remoteIfaceName: string | null;
  remoteChassisId: string | null;
  remoteMgmtIp: string | null;
  firstSeen: string;
  lastSeen: string;
  dismissed: boolean;
  linkedToType: 'PopLink' | 'DeviceLink' | null;
  linkedToId: number | null;
  device: { id: number; hostname: string; popId: number | null; pop: { name: string; uf: string | null } | null };
  suggested: { deviceId: number; hostname: string; popId: number | null; popName: string | null; matchedBy: 'lldpSystemName' | 'hostname' | 'mgmtIp' } | null;
};

export type DeviceLink = {
  id: number;
  deviceAId: number; deviceBId: number;
  ifaceAName: string | null; ifaceBName: string | null;
  type: string;
  state: 'UP' | 'DOWN' | 'DEGRADED' | 'UNKNOWN';
  lastSeen: string | null;
  lastChange: string | null;
  description: string | null;
  source?: string; approved?: boolean;
  deviceA: { id: number; hostname: string; mgmtIp: string; role: string; popId: number | null; vendor: { code: string } };
  deviceB: { id: number; hostname: string; mgmtIp: string; role: string; popId: number | null; vendor: { code: string } };
};

// F10 — Firmware
export type FirmwareKind = 'SYSTEM' | 'PATCH' | 'BUNDLE';
export type FirmwareRow = {
  id: number;
  kind: FirmwareKind;
  version: string;
  filename: string;
  sha256: string;
  sizeBytes: string;             // BigInt → string no JSON
  notes: string | null;
  createdAt: string;
  model: { id: number; code: string; family: string | null; driverClass: string; vendor: { id: number; code: string; name: string } };
  uploadedBy: { id: number; email: string } | null;
  _count: { deployments: number };
};
export type FirmwareDetail = FirmwareRow & {
  storagePath: string;
  modelId: number;
  uploadedById: number | null;
  deployments: FirmwareDeploymentDetail[];
};
export type FirmwareDeploymentStatus = 'PENDING' | 'TRANSFERRING' | 'TRANSFERRED' | 'ACTIVATING' | 'STAGED' | 'REBOOTING' | 'VERIFYING' | 'SUCCEEDED' | 'FAILED' | 'CANCELED';
export type FirmwareOperation = 'TRANSFER' | 'APPLY_SYSTEM' | 'APPLY_PATCH' | 'REBOOT' | 'CHECK_STARTUP';
export type FirmwareDeploymentDetail = {
  id: number;
  operation: FirmwareOperation;
  firmwareId: number | null;
  patchFirmwareId: number | null;     // deprecated 2026-05-28
  deviceId: number;
  status: FirmwareDeploymentStatus;
  jobId: string | null;
  startedById: number | null;
  startedAt: string;
  finishedAt: string | null;
  phaseStartedAt: string | null;
  errorPhase: string | null;
  errorMessage: string | null;
  verifiedVersion: string | null;
  remoteFilename: string | null;
  patchRemoteFilename: string | null; // deprecated
  progressFile: 'system' | 'patch' | null;
  progressBytes: string | null;
  progressTotal: string | null;
  progressPct: number | null;
  progressAt: string | null;
  log: string;
  firmware?:  { id: number; kind: FirmwareKind; version: string; filename: string; sha256: string; modelId?: number } | null;
  device?:    { id: number; hostname: string; mgmtIp?: string };
  startedBy?: { id: number; email: string } | null;
};

// F12 — Providers
export type ProviderKind = 'SWAP_FIBER' | 'POWER' | 'INTERNET_BACKUP' | 'TRANSPORT' | 'GENERATOR' | 'MAINTENANCE' | 'OTHER';
export type LinkOwnership = 'OWN' | 'SWAP' | 'TRANSPORT';
export type Provider = {
  id: number;
  code: string;
  name: string;
  kind: ProviderKind;
  description: string | null;
  website: string | null;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
};
export type ProviderRow = Provider & {
  _count: {
    contacts: number;
    procedures: number;
    attachments: number;
    popsAsPower: number;
    popLinks: number;
    deviceLinks: number;
  };
};
export type ProviderContact = {
  id: number;
  providerId: number;
  name: string;
  role: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  availability: string | null;
  priority: number;
  notes: string | null;
};
export type ProviderProcedure = {
  id: number;
  providerId: number;
  triggerType: string;
  title: string;
  description: string;
  slaMinutes: number | null;
  externalUrl: string | null;
  createdAt: string;
  updatedAt: string;
};
export type ProviderAttachment = {
  id: number;
  providerId?: number;
  filename: string;
  sha256: string;
  sizeBytes: number;
  contentType: string | null;
  uploadedById: number | null;
  notes: string | null;
  createdAt: string;
  uploadedBy?: { id: number; email: string } | null;
};
export type ProviderDetail = Provider & {
  contacts: ProviderContact[];
  procedures: ProviderProcedure[];
  attachments: ProviderAttachment[];
  _count: { popsAsPower: number; popLinks: number; deviceLinks: number };
};
export type ProviderVinculacoes = {
  popsAsPower: Array<{ id: number; code: string; name: string; uf: string | null; city: string | null; hasGenerator: boolean; hasUps: boolean; powerAccountNumber: string | null }>;
  popLinks: Array<{ id: number; providerCircuitId: string | null; monthlyCostCents: number | null; sourcePop: { id: number; code: string; name: string }; destPop: { id: number; code: string; name: string } }>;
  deviceLinks: Array<{ id: number; providerCircuitId: string | null; monthlyCostCents: number | null; deviceA: { id: number; hostname: string }; deviceB: { id: number; hostname: string } }>;
};
